var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import React, { useState } from "react";
import { Button, ConfirmationModal, ErrorModal, } from "@folio/stripes/components";
import { useCallout, useOkapiKy } from "@folio/stripes/core";
import { FormattedMessage, useIntl } from "react-intl";
import { useMutation, useQueryClient } from "react-query";
import { usePermissions } from "./usePermissions";
import { resetDeclaration } from "../api/publisherPrefixService";
import { getErrorCode } from "../utils/getErrorCode";
import { ErrorType } from "../types/ErrorType";
import { DeclarationStatusDto } from "../types/Api";
var RESET_ERROR_CODES = [
    ErrorType.DECLARATION_NOT_ALLOWED,
    ErrorType.DECLARATION_NOT_DECLARED,
];
/**
 * Action-menu entry "Zrušit deklaraci" (RCK-1677): an agency worker withdraws
 * a wrongly declared last used number of a publisher prefix after a
 * confirmation. The numbers blocked by the declaration are unblocked and the
 * prefix awaits a new declaration of its publisher. Shared by the prefix list
 * detail pane and the standalone prefix detail page.
 *
 * @param prefix  the publisher prefix shown in the detail
 * @param onReset called with the reset prefix once the declaration is withdrawn
 */
export var useResetDeclarationAction = function (prefix, onReset) {
    var _a;
    var ky = useOkapiKy();
    var callout = useCallout();
    var queryClient = useQueryClient();
    var formatMessage = useIntl().formatMessage;
    var canManageDeclaration = usePermissions().canManageDeclaration;
    var _b = useState(false), isConfirmOpen = _b[0], setConfirmOpen = _b[1];
    var _c = useState(null), errorMessage = _c[0], setErrorMessage = _c[1];
    var canShowResetAction = canManageDeclaration &&
        !!(prefix === null || prefix === void 0 ? void 0 : prefix.id) &&
        prefix.declarationStatus === DeclarationStatusDto.DECLARED;
    var resetMutation = useMutation(function (prefixId) { return resetDeclaration(ky, prefixId); }, {
        onSuccess: function (data) {
            setConfirmOpen(false);
            queryClient.invalidateQueries(["publisherPrefix", prefix === null || prefix === void 0 ? void 0 : prefix.id]);
            queryClient.invalidateQueries(["publisher-prefixes"]);
            callout === null || callout === void 0 ? void 0 : callout.sendCallout({
                type: "success",
                message: formatMessage({ id: "ui-br-agency.publisher-prefix.declare.reset.success" }, { unblocked: data.unblockedCount }),
            });
            onReset === null || onReset === void 0 ? void 0 : onReset(data.publisherPrefix);
        },
        onError: function (error) { return __awaiter(void 0, void 0, void 0, function () {
            var code, messageId;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        setConfirmOpen(false);
                        return [4 /*yield*/, getErrorCode(error)];
                    case 1:
                        code = _a.sent();
                        messageId = code && RESET_ERROR_CODES.includes(code)
                            ? "ui-br-agency.publisher-prefix.declare.reset.error.".concat(code)
                            : "ui-br-agency.publisher-prefix.declare.reset.error.generic";
                        setErrorMessage(formatMessage({ id: messageId }));
                        return [2 /*return*/];
                }
            });
        }); },
    });
    var renderResetAction = function (onToggle) {
        return canShowResetAction ? (React.createElement(Button, { buttonStyle: "dropdownItem", onClick: function () {
                onToggle === null || onToggle === void 0 ? void 0 : onToggle();
                setConfirmOpen(true);
            }, disabled: resetMutation.isLoading, fullWidth: true },
            React.createElement(FormattedMessage, { id: "ui-br-agency.publisher-prefix.declare.reset.action" }))) : null;
    };
    var resetModals = (React.createElement(React.Fragment, null,
        React.createElement(ConfirmationModal, { open: isConfirmOpen, heading: formatMessage({
                id: "ui-br-agency.publisher-prefix.declare.reset.modal.heading",
            }), message: formatMessage({ id: "ui-br-agency.publisher-prefix.declare.reset.modal.message" }, { value: (_a = prefix === null || prefix === void 0 ? void 0 : prefix.value) !== null && _a !== void 0 ? _a : "" }), confirmLabel: formatMessage({
                id: "ui-br-agency.publisher-prefix.declare.reset.modal.confirm",
            }), cancelLabel: formatMessage({ id: "ui-br-agency.button.cancel" }), isConfirmButtonDisabled: resetMutation.isLoading, onConfirm: function () { return (prefix === null || prefix === void 0 ? void 0 : prefix.id) && resetMutation.mutate(prefix.id); }, onCancel: function () { return setConfirmOpen(false); } }),
        React.createElement(ErrorModal, { open: errorMessage !== null, label: formatMessage({ id: "ui-br-agency.modal.label.error" }), content: errorMessage !== null && errorMessage !== void 0 ? errorMessage : "", onClose: function () { return setErrorMessage(null); } })));
    return { canShowResetAction: canShowResetAction, renderResetAction: renderResetAction, resetModals: resetModals };
};
